import type { KnowledgeContentKind, KnowledgeSourceItem } from '../admin/knowledge/knowledgeAdmin';

export type TeamSourceMode = 'document' | 'notes';

export interface TeamSourceType {
    mode: TeamSourceMode;
    contentKind: KnowledgeContentKind;
    label: string;
    description: string;
    icon: string;
}

export const TEAM_SOURCE_TYPES: TeamSourceType[] = [
    {
        mode: 'document',
        contentKind: 'reference',
        label: 'Document',
        description: 'Upload a past brief, report, or study',
        icon: 'file alternate outline',
    },
    {
        mode: 'notes',
        contentKind: 'org_context',
        label: 'Team notes',
        description: 'Paste facts about your organisation or market',
        icon: 'building outline',
    },
];

export function teamTypeLabel(item: KnowledgeSourceItem): string {
    if (item.content_kind === 'org_context') return 'Team notes';
    if (item.file_name) return 'Document';
    return 'Reference';
}

export function teamTypeForKind(kind: KnowledgeContentKind): TeamSourceType {
    return (
        TEAM_SOURCE_TYPES.find((t) => t.contentKind === kind) ??
        TEAM_SOURCE_TYPES[0]
    );
}

export function formatTeamKnowledgeDate(value: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
}

export function formatTeamFileSize(bytes: number | null): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Plain-language status for the team UI */
export function teamKnowledgeStatusLabel(status: KnowledgeSourceItem['status']): string {
    switch (status) {
        case 'published':
            return 'Ready';
        case 'processing':
            return 'Indexing…';
        case 'failed':
            return 'Indexing failed';
        case 'archived':
            return 'Archived';
        default:
            return 'Preparing';
    }
}

export function teamKnowledgeStatusTone(status: KnowledgeSourceItem['status']): 'success' | 'info' | 'danger' | 'warn' | 'neutral' {
    switch (status) {
        case 'published':
            return 'success';
        case 'processing':
            return 'info';
        case 'failed':
            return 'danger';
        case 'archived':
            return 'warn';
        default:
            return 'neutral';
    }
}

export function teamTileIcon(item: KnowledgeSourceItem): string {
    if (item.file_name) return 'file alternate outline';
    return 'sticky note outline';
}

export function teamKnowledgeFailedHint(item: KnowledgeSourceItem): string | null {
    if (item.status !== 'failed') return null;
    const raw = item.metadata?.ingest_error;
    if (typeof raw !== 'string' || !raw.trim()) {
        return 'Indexing did not complete. Try Retry, or re-upload the file.';
    }
    if (raw.includes('Malformed UTF-8')) {
        return 'The file text could not be read cleanly. Try Retry after saving, or upload a plain-text copy.';
    }
    if (raw.includes('could not find driver')) {
        return 'Indexing service was temporarily unavailable. Try Retry.';
    }
    return 'Indexing did not complete. Try Retry.';
}
